import type { HubState } from '../state';
import type { AppState, HubPersonasMap } from '../../appstate';
import { HubPersona, hasEmail, createSelector, PersonType, sum } from '../../shared';
import { getCurrentHubPersonas } from './currentPersonaSelector';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isHubPersonaWritable } from './personaSelectorUtils';

export type { HubPersona };

/**
 * Returns whether there are too many currentPersonas for the delete action
 */
export const isTooManyCurrentPersonasForDelete = createSelector<
    AppState,
    HubState,
    number,
    HubPersona[],
    boolean
>(
    'isTooManyCurrentPersonasForDelete',
    [
        (appState: AppState) => appState.data.userConfiguration.userOptions.MaxPersonasToDelete,
        getCurrentHubPersonas,
    ],
    (maxPersonasToDelete, currentHubPersonas) => {
        return currentHubPersonas.length > maxPersonasToDelete;
    }
);

/**
 * Returns whether there are too many contacts in currentPersonas for the link action. Logic is copied from
 * //depot/Chicago/sources/Dev/Owa/src/Client/Core/PeopleHub/ViewModels/PeopleCommandBarViewModel.cs
 */
export const isTooManyCurrentPersonasForLinking = createSelector<
    AppState,
    HubState,
    number,
    HubPersona[],
    boolean
>(
    'isTooManyCurrentPersonasForLinking',
    [
        (appState: AppState) =>
            appState.data.userConfiguration.userOptions.MaximumNumberOfContactsPerPerson,
        getCurrentHubPersonas,
    ],
    (maximumNumberOfContactsPerPerson, currentHubPersonas) => {
        return (
            sum(
                currentHubPersonas.map(persona =>
                    persona.type === PersonType.Person ? persona.totalNumberOfLinkedContacts : 0
                )
            ) > maximumNumberOfContactsPerPerson
        );
    }
);

/**
 * Returns whether there are too many GAL contacts in currentPersonas for the link action. Logic is copied from
 * //depot/Chicago/sources/Dev/Owa/src/Client/Core/PeopleHub/ViewModels/PeopleCommandBarViewModel.cs
 */
export const isTooManyGalCurrentPersonasForLinking = createSelector<
    AppState,
    HubState,
    HubPersona[],
    boolean
>('isTooManyGalCurrentPersonasForLinking', [getCurrentHubPersonas], currentHubPersonas => {
    return (
        currentHubPersonas.filter(
            persona =>
                persona.type === PersonType.Person &&
                persona.info.GALContactGuid &&
                persona.info.GALContactGuid !== null
        ).length > 1
    );
});

/**
 * Returns whether all currentPersonas have linked contacts
 */
export const areAllCurrentPersonasLinked = createSelector<
    AppState,
    HubState,
    HubPersona[],
    boolean
>('areAllCurrentPersonasLinked', [getCurrentHubPersonas], currentHubPersonas =>
    currentHubPersonas.every(
        persona =>
            persona.type === PersonType.Person &&
            persona.linkedContacts &&
            persona.linkedContacts.length > 1
    )
);

/**
 * Returns whether all the currentPersonas are of type Person
 */
export const areAllCurrentPersonasTypePerson = createSelector<
    AppState,
    HubState,
    HubPersona[],
    boolean
>('areAllCurrentPersonasTypePerson', [getCurrentHubPersonas], currentHubPersonas => {
    return currentHubPersonas.every(persona => persona.type === PersonType.Person);
});

/**
 * Returns whether all the currentPersonas can be favorited/unfavorited
 */
export const areAllCurrentPersonasFavoritable = createSelector<
    AppState,
    HubState,
    HubPersona[],
    boolean
>('areAllCurrentPersonasTypeFavoritePerson', [getCurrentHubPersonas], currentHubPersonas => {
    const isFavoritePdlSupported =
        isFeatureEnabled('tri-favorites-roaming') && isFeatureEnabled('peo-favoritePdls');

    return currentHubPersonas.every(
        persona =>
            persona.type === PersonType.FavoritePerson ||
            persona.type === PersonType.Person ||
            (isFavoritePdlSupported &&
                (persona.type === PersonType.FavoritePrivateDistributionList ||
                    persona.type === PersonType.PrivateDistributionList))
    );
});

/**
 * Returns whether some of the currentPersonas are of type FavoritePerson
 */
export const areSomeCurrentPersonasTypeFavoritePerson = createSelector<
    AppState,
    HubState,
    HubPersona[],
    boolean
>('areSomeCurrentPersonasTypeFavoritePerson', [getCurrentHubPersonas], currentHubPersonas => {
    return (currentHubPersonas || []).some(persona => persona.type === PersonType.FavoritePerson);
});

export const areSomeCurrentPersonasTypePublicDistributionList = createSelector<
    AppState,
    HubState,
    HubPersona[],
    boolean
>('areSomeCurrentPersonasTypePublicDistributionList', [getCurrentHubPersonas], currentHubPersonas =>
    (currentHubPersonas || []).some(persona => persona.type === PersonType.PublicDistributionList)
);

/**
 * Returns whether all the currentPersonas are of type FavoritePerson
 */
export const areAllCurrentPersonasTypePersonaOrPDL = createSelector<
    AppState,
    HubState,
    HubPersona[],
    boolean
>('areAllCurrentPersonasTypePersonaOrPDL', [getCurrentHubPersonas], currentHubPersonas => {
    return currentHubPersonas.every(
        persona =>
            persona.type === PersonType.Person ||
            persona.type === PersonType.PrivateDistributionList
    );
});

/**
 * Returns whether all the currentPersonas contains at least one writable contact
 */
export const areAllCurrentPersonasWritable = createSelector<
    AppState,
    HubState,
    HubPersona[],
    HubPersonasMap,
    boolean
>(
    'areAllCurrentPersonasWritable',
    [getCurrentHubPersonas, (appState: AppState) => appState.data.personas],
    (currentHubPersonas, hubPersonasMap) =>
        (currentHubPersonas || []).every(hubPersona =>
            isHubPersonaWritable(hubPersona, hubPersonasMap)
        )
);

/**
 * Returns whether some currentPersonas are writable (not read-only)
 */
export const areSomeCurrentPersonasWritable = createSelector<
    AppState,
    HubState,
    HubPersona[],
    HubPersonasMap,
    boolean
>(
    'areSomeCurrentPersonasWritable',
    [getCurrentHubPersonas, (appState: AppState) => appState.data.personas],
    (currentHubPersonas, hubPersonasMap) =>
        (currentHubPersonas || []).some(hubPersona =>
            isHubPersonaWritable(hubPersona, hubPersonasMap)
        )
);

/**
 * Returns whether there is zero currentPersonas
 */
export const isZeroCurrentPersona = createSelector<AppState, HubState, HubPersona[], boolean>(
    'isZeroCurrentPersona',
    [getCurrentHubPersonas],
    currentHubPersonas => currentHubPersonas.length === 0
);

/**
 * Returns whether there is one currentPersona
 */
export const isOneCurrentPersona = createSelector<AppState, HubState, HubPersona[], boolean>(
    'isOneCurrentPersona',
    [getCurrentHubPersonas],
    currentHubPersonas => currentHubPersonas.length === 1
);

/**
 * Returns whether all the currentPersonas have an email address.
 * For PDLs, it checks whether all "Person" members have emails.
 */
export const getCurrentPersonasHaveEmails = createSelector<
    AppState,
    HubState,
    HubPersona[],
    boolean
>('getCurrentPersonasHaveEmails', [getCurrentHubPersonas], currentHubPersonas =>
    currentHubPersonas.every(hasEmail)
);

/**
 * Returns whether all the currentPersonas have an IM address.
 * For PDLs, it returns false: there is currently no support for starting chats with PDL members.
 */
export const getCurrentPersonasHaveImAddresses = createSelector<
    AppState,
    HubState,
    HubPersona[],
    boolean
>('getCurrentPersonasHaveImAddresses', [getCurrentHubPersonas], currentHubPersonas =>
    currentHubPersonas.every(hasImAddress)
);

/**
 * Returns whether the currentPersona has an IM address.
 * For PDLs, it returns false: there is no support for starting chats with PDL members.
 */
function hasImAddress(persona: HubPersona): boolean {
    switch (persona.type) {
        case PersonType.Person:
        case PersonType.Generic:
            return !!persona.info.imAddress;
        default:
            return false;
    }
}

/**
 * Returns whether there are too many personas selected for Move operation. We set size to be equal to be same size for MaxPersonasToDelete.
 * Because there is no config for Move max size, and it looks like there are similiar restrictions from backend.
 * So let's have it, to be extra safe.
 */
export const isTooManyCurrentPersonasForMove = createSelector<
    AppState,
    HubState,
    number,
    HubPersona[],
    boolean
>(
    'isTooManyCurrentPersonasForMove',
    [
        (appState: AppState) => appState.data.userConfiguration.userOptions.MaxPersonasToDelete,
        getCurrentHubPersonas,
    ],
    (maxPersonasToDelete, currentHubPersonas) => {
        return currentHubPersonas.length > maxPersonasToDelete;
    }
);

/**
 * MaxSize synced with maxPersonasToDelete intentionally
 */
export const isTooManyCurrentPersonasForHardDelete = createSelector<
    AppState,
    HubState,
    number,
    HubPersona[],
    boolean
>(
    'isTooManyCurrentPersonasForHardDelete',
    [
        (appState: AppState) => appState.data.userConfiguration.userOptions.MaxPersonasToDelete,
        getCurrentHubPersonas,
    ],
    (maxPersonasToDelete, currentHubPersonas) => {
        return currentHubPersonas.length > maxPersonasToDelete;
    }
);

/**
 * MaxSize synced with maxPersonasToDelete intentionally
 */
export const isTooManyCurrentPersonasForRestore = createSelector<
    AppState,
    HubState,
    number,
    HubPersona[],
    boolean
>(
    'isTooManyCurrentPersonasForRestore',
    [
        (appState: AppState) => appState.data.userConfiguration.userOptions.MaxPersonasToDelete,
        getCurrentHubPersonas,
    ],
    (maxPersonasToDelete, currentHubPersonas) => {
        return currentHubPersonas.length > maxPersonasToDelete;
    }
);
export type { ClickedHubPersonaState } from '../../appstate';
